import { gql } from '@apollo/client'

const PROJECT_ATTRIBUTES = gql`
  fragment projectAttributes on Project {
    id
    createdAt
    updatedAt

    name
    status
    address
    startDate
    timezone
    locale
    language
    endDate

    projectContacts {
      id
      name
      number
      email
      type
      customType
    }

    projectAdmins {
      id
      user {
        id
        firstName
        lastName
      }
    }

    requiredTickets {
      id
      shortName
      name
    }

    builder {
      builderMetadata
      company {
        name
      }
      requiredTickets {
        id
        shortName
        name
      }
    }

    subcontractors {
      id
      isBuilder
    }

    documents {
      id
    }

    info
    config
    procoreId
    latitude
    longitude
    radius

    integrationConnections {
      id
      integrationId
      integrationIdentifier
      integration {
        integrationType
      }
      sourceId
    }

    siteTimings {
      dayOfWeek
      startTime
      endTime
    }

    tags {
      id
      slug
      name
    }
  }
`
const PROJECT_ATTRIBUTES_SUBSET = gql`
  fragment projectAttributesSubset on Project {
    id
    name
    address
    status
    createdAt
    timezone
    locale
    language
    latitude
    longitude

    projectAddress {
      country
      state
    }

    projectAdmins {
      id
      user {
        firstName
        lastName
      }
    }

    procoreId
    integrationConnections {
      id
      integrationIdentifier
      integration {
        integrationType
      }
      sourceId
    }

    tags {
      id
      name
      slug
    }
  }
`

export const GET_PROJECTS = gql`
  query projects_UI($sort: ProjectSortInput) {
    projects(sort: $sort) {
      ...projectAttributes
    }
  }
  ${PROJECT_ATTRIBUTES}
`

export const GET_PROJECTS_WITH_INTEGRATIONS = gql`
  query projectsWithIntegrations_UI {
    projects {
      id
      name
      integrationConnections {
        id
        integrationIdentifier # buildxact job id
        integration {
          integrationType # BUILDXACT or PROCORE
        }
        sourceId
      }
    }
  }
`

export const GET_PROJECTS_LIST = gql`
  query projectsList_UI($sort: ProjectSortInput, $filter: ProjectFilterInput) {
    projects(sort: $sort, filter: $filter) {
      ...projectAttributesSubset
    }
  }
  ${PROJECT_ATTRIBUTES_SUBSET}
`

export const GET_PROJECTS_FOR_BUILDER_LIST = gql`
  query projectsForBuilderList_UI(
    $filter: ProjectsForBuilderFilterInput
    $pagination: PaginationInput
  ) {
    projectsForBuilder(filter: $filter, pagination: $pagination) {
      id
      name
      status
    }
  }
`

export const GET_PROJECTS_FOR_BUILDER_WITH_AGG = gql`
  query projectsForBuilderWithAgg_UI(
    $filter: ProjectsForBuilderFilterInput
    $sort: ProjectsForBuilderSortInput
    $pagination: PaginationInput
  ) {
    projectsForBuilder(filter: $filter, sort: $sort, pagination: $pagination) {
      ...projectAttributesSubset
    }
    projectsForBuilderAggregation(filter: $filter, pagination: $pagination) {
      totalCount
      totalPages
      offset
      limit
    }
  }
  ${PROJECT_ATTRIBUTES_SUBSET}
`

export const GET_PROJECTS_FOR_BUILDER_FILTER_AGGREGATION = gql`
  query projectsForBuilderFilterAggregation_UI {
    projectsForBuilderFilterAggregation {
      inProgress
      upcoming
      complete
      archived
    }
  }
`

export const SA_GET_PROJECTS_BY_BUILDER = gql`
  query saProjectsByBuilder_UI($builderId: String) {
    saProjectsByBuilder(builderId: $builderId) {
      ...projectAttributes
    }
  }
  ${PROJECT_ATTRIBUTES}
`

export const GET_PROJECT = gql`
  query project_UI($projectId: String!) {
    project(id: $projectId) {
      ...projectAttributes
    }
    projectSpecificSettings(id: $projectId) {
      qrCodeRotationEnabled
      qrCodeRotationInterval
      qrCodeRotationCustomMessage
      manualQrCodeRotationProjectCode
    }
  }
  ${PROJECT_ATTRIBUTES}
`

export const GET_PROJECT_CONTACTS = gql`
  query projectContacts_UI($projectId: String!) {
    project(id: $projectId) {
      projectContacts {
        id
        name
        number
        email
        type
        customType
      }

      projectAdmins {
        id
        user {
          firstName
          lastName
        }
      }
    }
  }
`

export const GET_PUBLIC_PROJECT = gql`
  query publicProject_UI($projectId: String!) {
    publicProject(id: $projectId) {
      id
      createdAt
      name
      address
      timezone
      locale
      language
      country
      status
      latitude
      longitude
      builder {
        id
        logoUrl
        builderMetadata
        company {
          name
        }
        hideBuildPassLogos
        defaultLocale
        defaultTimezone
      }
      projectContacts {
        id
        name
        number
        type
        customType
      }
      signOnRedirectUrl
      timesheetsEnabled
      defectsEnabled
      drawingsEnabled
      workerChecklistsEnabled
      permitsEnabled
      actionsEnabled
      siteTimings {
        dayOfWeek
        startTime
        endTime
      }
      visitorDocumentAccessEnabled
      qrCodeRotationEnabled
      qrCodeRotationCustomMessage
      swmsBuilderEnabled
    }
  }
`

export const GET_PUBLIC_PROJECTS_FOR_SUBCONTRACTOR = gql`
  query publicProjectsForSubcontractor_UI($subcontractorId: String!) {
    publicProjectsForSubcontractor(subcontractorId: $subcontractorId) {
      id
      name
      status
      timezone
      locale
      builder {
        id
        logoUrl
        company {
          name
        }
        defaultTimezone
        defaultLocale
      }
    }
  }
`

export const CREATE_PROJECT = gql`
  mutation createProject_UI(
    $projectName: String!
    $projectAddress: String!
    $projectStatus: ProjectStatus!
    $projectAdminsIds: [String]
    $requiredTicketsIds: [String]
    $signOnRedirectUrl: String
    $projectContacts: [ContactInput]
    $startDate: DateTime
    $endDate: DateTime
    $locale: SupportedLocaleCountryCode
    $timezone: String
  ) {
    createProject(
      input: {
        name: $projectName
        status: $projectStatus
        address: $projectAddress
        projectAdminsIds: $projectAdminsIds
        requiredTicketsIds: $requiredTicketsIds
        signOnRedirectUrl: $signOnRedirectUrl
        projectContacts: $projectContacts
        startDate: $startDate
        endDate: $endDate
        locale: $locale
        timezone: $timezone
      }
    ) {
      ...projectAttributes
    }
  }
  ${PROJECT_ATTRIBUTES}
`

export const UPDATE_PROJECT = gql`
  mutation updateProject_UI(
    $projectName: String
    $projectAddress: String
    $projectStatus: ProjectStatus
    $projectId: String!
    $projectAdminsIds: [String]
    $requiredTicketsIds: [String]
    $signOnRedirectUrl: String
    $projectContacts: [UpdateContactInput]
    $startDate: DateTime
    $endDate: DateTime
    $latitude: Float
    $longitude: Float
    $radius: Float
    $info: JSON
    $timezone: String
    $locale: SupportedLocaleCountryCode
    $language: String
  ) {
    updateProject(
      input: {
        name: $projectName
        status: $projectStatus
        address: $projectAddress
        projectAdminsIds: $projectAdminsIds
        requiredTicketsIds: $requiredTicketsIds
        signOnRedirectUrl: $signOnRedirectUrl
        projectContacts: $projectContacts
        startDate: $startDate
        endDate: $endDate
        latitude: $latitude
        longitude: $longitude
        radius: $radius
        info: $info
        timezone: $timezone
        locale: $locale
        language: $language
      }
      id: $projectId
    ) {
      ...projectAttributes
    }
  }
  ${PROJECT_ATTRIBUTES}
`

export const ARCHIVE_PROJECT = gql`
  mutation archiveProject_UI($id: String!) {
    updateProject(input: { status: ARCHIVED }, id: $id) {
      ...projectAttributes
    }
  }
  ${PROJECT_ATTRIBUTES}
`

export const CONNECT_PROCORE_PROJECT = gql`
  mutation connectProcoreProject_UI($id: String!, $input: ProcoreProjectInput) {
    connectProcoreProject(id: $id, input: $input) {
      id
      procoreId
    }
  }
`

export const DISCONNECT_PROCORE_PROJECT = gql`
  mutation disconnectProcoreProject_UI($id: String!) {
    disconnectProcoreProject(id: $id) {
      id
      procoreId
    }
  }
`

export const IMPORT_INTEGRATION_PROJECTS = gql`
  mutation importIntegrationProjects_UI(
    $integrationType: IntegrationType!
    $integrationIdentifiers: [String!]!
  ) {
    importIntegrationProjects(
      integrationType: $integrationType
      integrationIdentifiers: $integrationIdentifiers
    ) {
      id
    }
  }
`

const NESTED_PROJECT_LOCATION_ATTRIBUTE = gql`
  fragment nestedProjectLocationAttributes on ProjectLocation {
    id
    name
    order
  }
`

const NESTED_PROJECT_LOCATION_RESOURCE_LINK_ATTRIBUTE = gql`
  fragment nestedProjectLocationResourceLinkAttributes on ProjectLocation_ResourceLink {
    id
    name
    order
    fullLocationName
  }
`

export const PROJECT_LOCATION_WITH_CHILDREN_ATTRIBUTES = gql`
  fragment projectLocationWithChildrenAttributes on ProjectLocation {
    ...nestedProjectLocationAttributes
    children {
      ...nestedProjectLocationAttributes
      children {
        ...nestedProjectLocationAttributes
        children {
          ...nestedProjectLocationAttributes
          children {
            ...nestedProjectLocationAttributes
            children {
              ...nestedProjectLocationAttributes
              children {
                ...nestedProjectLocationAttributes
                children {
                  ...nestedProjectLocationAttributes
                  children {
                    ...nestedProjectLocationAttributes
                    children {
                      ...nestedProjectLocationAttributes
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${NESTED_PROJECT_LOCATION_ATTRIBUTE}
`

export const PROJECT_LOCATION_COUNT_ATTRIBUTES = gql`
  fragment projectLocationCountAttributes on ProjectLocation {
    defectsCount
    checklistsCount
    itpsCount
    platformActionsCount
    permitsCount
  }
`

export const NESTED_PROJECT_LOCATION_PUBLIC_ATTRIBUTE = gql`
  fragment nestedProjectLocationPublicAttributes on ProjectLocation_Public {
    id
    name
    order
  }
`

export const PROJECT_LOCATION_PUBLIC_WITH_CHILDREN_ATTRIBUTES = gql`
  fragment projectLocationPublicWithChildrenAttributes on ProjectLocation_Public {
    ...nestedProjectLocationPublicAttributes
    children {
      ...nestedProjectLocationPublicAttributes
      children {
        ...nestedProjectLocationPublicAttributes
        children {
          ...nestedProjectLocationPublicAttributes
          children {
            ...nestedProjectLocationPublicAttributes
            children {
              ...nestedProjectLocationPublicAttributes
              children {
                ...nestedProjectLocationPublicAttributes
                children {
                  ...nestedProjectLocationPublicAttributes
                  children {
                    ...nestedProjectLocationPublicAttributes
                    children {
                      ...nestedProjectLocationPublicAttributes
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${NESTED_PROJECT_LOCATION_PUBLIC_ATTRIBUTE}
`

export const PROJECT_LOCATION_LIST_WITH_CHILDREN_ATTRIBUTES = gql`
  fragment projectLocationListWithChildrenAttributes on ProjectLocation {
    ...nestedProjectLocationAttributes
    ...projectLocationCountAttributes
    children {
      ...nestedProjectLocationAttributes
      ...projectLocationCountAttributes
      children {
        ...nestedProjectLocationAttributes
        ...projectLocationCountAttributes
        children {
          ...nestedProjectLocationAttributes
          ...projectLocationCountAttributes
          children {
            ...nestedProjectLocationAttributes
            ...projectLocationCountAttributes
            children {
              ...nestedProjectLocationAttributes
              ...projectLocationCountAttributes
              children {
                ...nestedProjectLocationAttributes
                ...projectLocationCountAttributes
                children {
                  ...nestedProjectLocationAttributes
                  ...projectLocationCountAttributes
                  children {
                    ...nestedProjectLocationAttributes
                    ...projectLocationCountAttributes
                    children {
                      ...nestedProjectLocationAttributes
                      ...projectLocationCountAttributes
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${NESTED_PROJECT_LOCATION_ATTRIBUTE}
  ${PROJECT_LOCATION_COUNT_ATTRIBUTES}
`

export const PROJECT_LOCATION_WITH_PARENT_ATTRIBUTES = gql`
  fragment projectLocationWithParentAttributes on ProjectLocation {
    ...nestedProjectLocationAttributes
    parent {
      ...nestedProjectLocationAttributes
      parent {
        ...nestedProjectLocationAttributes
        parent {
          ...nestedProjectLocationAttributes
          parent {
            ...nestedProjectLocationAttributes
            parent {
              ...nestedProjectLocationAttributes
              parent {
                ...nestedProjectLocationAttributes
                parent {
                  ...nestedProjectLocationAttributes
                  parent {
                    ...nestedProjectLocationAttributes
                    parent {
                      ...nestedProjectLocationAttributes
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${NESTED_PROJECT_LOCATION_ATTRIBUTE}
`

export const PROJECT_LOCATION_RESOURCE_LINK_WITH_PARENT_ATTRIBUTES = gql`
  fragment projectLocationResourceLinkWithParentAttributes on ProjectLocation_ResourceLink {
    ...nestedProjectLocationResourceLinkAttributes
    parent {
      ...nestedProjectLocationResourceLinkAttributes
      parent {
        ...nestedProjectLocationResourceLinkAttributes
        parent {
          ...nestedProjectLocationResourceLinkAttributes
          parent {
            ...nestedProjectLocationResourceLinkAttributes
            parent {
              ...nestedProjectLocationResourceLinkAttributes
              parent {
                ...nestedProjectLocationResourceLinkAttributes
                parent {
                  ...nestedProjectLocationResourceLinkAttributes
                  parent {
                    ...nestedProjectLocationResourceLinkAttributes
                    parent {
                      ...nestedProjectLocationResourceLinkAttributes
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${NESTED_PROJECT_LOCATION_RESOURCE_LINK_ATTRIBUTE}
`

export const GET_PROJECT_LOCATIONS = gql`
  query projectLocations_UI($projectId: String!) {
    projectLocations(projectId: $projectId) {
      ...projectLocationWithChildrenAttributes
    }
  }
  ${PROJECT_LOCATION_WITH_CHILDREN_ATTRIBUTES}
`

export const GET_PROJECT_LOCATIONS_PUBLIC = gql`
  query projectLocationsPublic_UI($projectId: String!, $slug: String) {
    projectLocations_public(projectId: $projectId, slug: $slug) {
      ...projectLocationPublicWithChildrenAttributes
    }
  }
  ${PROJECT_LOCATION_PUBLIC_WITH_CHILDREN_ATTRIBUTES}
`

export const GET_PROJECT_LOCATIONS_LIST = gql`
  query projectLocationsList_UI($projectId: String!) {
    projectLocations(projectId: $projectId) {
      ...projectLocationListWithChildrenAttributes
    }
  }
  ${PROJECT_LOCATION_LIST_WITH_CHILDREN_ATTRIBUTES}
`

export const GET_ALL_PROJECT_LOCATIONS = gql`
  query allProjectLocations_UI {
    projects {
      id
      name
      locations {
        ...projectLocationWithChildrenAttributes
      }
    }
  }
  ${PROJECT_LOCATION_WITH_CHILDREN_ATTRIBUTES}
`

export const CREATE_PROJECT_LOCATION = gql`
  mutation createProjectLocation_UI($input: CreateProjectLocationInput!) {
    createProjectLocation(input: $input) {
      ...projectLocationWithChildrenAttributes
    }
  }
  ${PROJECT_LOCATION_WITH_CHILDREN_ATTRIBUTES}
`

export const UPDATE_PROJECT_LOCATION = gql`
  mutation updateProjectLocation_UI(
    $id: String!
    $input: UpdateProjectLocationInput!
  ) {
    updateProjectLocation(id: $id, input: $input) {
      ...projectLocationWithChildrenAttributes
    }
  }
  ${PROJECT_LOCATION_WITH_CHILDREN_ATTRIBUTES}
`

export const DELETE_PROJECT_LOCATION = gql`
  mutation deleteProjectLocation_UI($id: String!) {
    deleteProjectLocation(id: $id) {
      id
    }
  }
`

export const SET_PROJECT_SITE_TIMINGS = gql`
  mutation setProjectSiteTimings_UI(
    $id: String!
    $input: SetProjectTimingsInput!
  ) {
    setProjectSiteTimings(id: $id, input: $input) {
      dayOfWeek
      startTime
      endTime
    }
  }
`

export const GET_PROJECT_STATS = gql`
  query projectStats_UI($projectId: String!) {
    projectStats(id: $projectId) {
      signOnCount
      inductionCount
      workerCount
      subcontractorCount
    }
  }
`

export const UPDATE_PROJECT_SPECIFIC_SETTINGS = gql`
  mutation updateProjectSpecificSettings_UI(
    $projectId: String!
    $input: UpdateProjectSpecificSettingsInput!
  ) {
    updateProjectSpecificSettings(id: $projectId, input: $input) {
      qrCodeRotationEnabled
      qrCodeRotationInterval
    }
  }
`
